import QRCode from 'qrcode';
import type {
  CriticalSession,
  DraftRecord,
  ItemResponse,
  PhotoRecord,
  ProactiveReport,
  ResponseState,
} from './types';
import { eventLevels, proactiveSource, relatedCategories, sifDefinition } from './data/proactive';

export const id = (prefix: string) =>
  `${prefix}_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 8)}`;

export const today = () => new Date().toISOString().slice(0, 10);

export const currentTime = () =>
  new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: false });

export const escapeHtml = (value = '') =>
  value
    .replaceAll('&', '&amp;')
    .replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;')
    .replaceAll('"', '&quot;')
    .replaceAll("'", '&#039;');

export const responseLabel = (state?: ResponseState) => {
  if (state === 'protected') return 'Protected';
  if (state === 'exposed') return 'Exposed';
  if (state === 'na') return 'N/A';
  return 'Not answered';
};

export const activeResponses = (responses: ItemResponse[]) =>
  responses.filter((response) => !response.questionSnapshot.inactive);

export const photoTotal = (record: DraftRecord) => record.photos.length;

export const photoCountLabel = (count: number) => `${count} photo${count === 1 ? '' : 's'}`;

export const photosForResponse = (photos: PhotoRecord[], response: ItemResponse, controlId?: string) =>
  photos.filter((photo) => {
    if (!response.photoIds.includes(photo.id)) return false;
    if (photo.associatedItemId && photo.associatedItemId !== response.itemId) return false;
    if (controlId && photo.associatedControlId && photo.associatedControlId !== controlId) return false;
    return true;
  });

const photoLabelFor = (photo: PhotoRecord, fallback: string) => photo.label || photo.name || fallback;

export const currentAppUrl = () => {
  if (typeof window === 'undefined' || !window.location?.origin) return 'Critical Control Hub URL not configured';
  return `${window.location.origin}/`;
};

const qrDataUri = (target: string) => {
  if (!target.startsWith('http')) return '';
  const qr = QRCode.create(target, { errorCorrectionLevel: 'M' });
  const size = qr.modules.size;
  const cells: string[] = [];
  for (let y = 0; y < size; y += 1) {
    for (let x = 0; x < size; x += 1) {
      if (qr.modules.get(x, y)) cells.push(`<rect x="${x}" y="${y}" width="1" height="1"/>`);
    }
  }
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${size} ${size}" role="img" aria-label="Critical Control Hub QR code"><rect width="${size}" height="${size}" fill="#fff"/><g fill="#111827">${cells.join('')}</g></svg>`;
  return `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`;
};

export const compressImage = (file: File): Promise<string> =>
  new Promise((resolve, reject) => {
    if (!file.type.startsWith('image/')) {
      reject(new Error('Only image files can be attached.'));
      return;
    }
    const reader = new FileReader();
    reader.onerror = () => reject(new Error('Unable to read selected image.'));
    reader.onload = () => {
      const image = new Image();
      image.onerror = () => reject(new Error('Unable to process selected image.'));
      image.onload = () => {
        const maxSide = 1400;
        const ratio = Math.min(1, maxSide / Math.max(image.width, image.height));
        const canvas = document.createElement('canvas');
        canvas.width = Math.max(1, Math.round(image.width * ratio));
        canvas.height = Math.max(1, Math.round(image.height * ratio));
        const context = canvas.getContext('2d');
        if (!context) {
          reject(new Error('Image processing is not supported in this browser.'));
          return;
        }
        context.drawImage(image, 0, 0, canvas.width, canvas.height);
        resolve(canvas.toDataURL('image/jpeg', 0.78));
      };
      image.src = String(reader.result);
    };
    reader.readAsDataURL(file);
  });

export const createPhoto = async (
  file: File,
  associationType: PhotoRecord['associationType'],
  associatedItemId?: string,
  label?: string,
  associatedControlId?: string,
): Promise<PhotoRecord> => ({
  id: id('photo'),
  dataUrl: await compressImage(file),
  label,
  associationType,
  associatedControlId,
  associatedItemId,
  createdAt: new Date().toISOString(),
  name: file.name,
});

export const validateCriticalSession = (session: CriticalSession) => {
  const issues: { key: string; label: string; message: string }[] = [];
  const shared = session.sharedDetails;
  [
    ['observer', 'Session details', 'Name is required.'],
    ['pcBranch', 'Session details', 'PC / Branch number is required.'],
  ].forEach(([field, label, message]) => {
    if (!String(shared[field as keyof typeof shared] || '').trim()) issues.push({ key: field, label, message });
  });
  (shared.additionalParticipants || []).forEach((participant, index) => {
    if (!participant.name.trim()) {
      issues.push({
        key: `participant_${participant.id}_name`,
        label: 'Session details',
        message: `Participant ${index + 1} name is required.`,
      });
    }
  });
  session.controlInstances.forEach((control) => {
    control.definitionSnapshot.headerFields.forEach((field) => {
      if (field.required && !String(control.editableDetails[field.id] || '').trim()) {
        issues.push({
          key: `${control.id}_${field.id}`,
          label: control.definitionSnapshot.title,
          message: `${field.label} is required.`,
        });
      }
    });
    activeResponses(control.responses).forEach((response) => {
      const prefix = `${control.definitionSnapshot.title} item ${response.questionSnapshot.number}`;
      if (!response.state) {
        issues.push({
          key: response.itemId,
          label: prefix,
          message: 'Select Protected, Exposed, or N/A.',
        });
      }
      if (response.state === 'exposed' && !String(response.comment || '').trim()) {
        issues.push({
          key: response.itemId,
          label: prefix,
          message: 'Exposed requires a comment before completion.',
        });
      }
    });
  });
  return issues;
};

export const validateProactive = (report: ProactiveReport) => {
  const issues: { key: string; label: string; message: string }[] = [];
  const required: [keyof ProactiveReport['fields'], string][] = [
    ['pcNumber', 'PC #'],
    ['date', 'Date'],
    ['time', 'Time'],
    ['name', 'Name'],
    ['location', 'Location of Event'],
    ['eventType', 'Event'],
    ['eventLevel', 'Event Level'],
    ['observation', 'Briefly describe your observation'],
    ['correctiveAction', 'What was done to correct the situation?'],
  ];
  issues.push(
    ...required
    .filter(([key]) => !String(report.fields[key] || '').trim())
    .map(([key, label]) => ({ key, label: 'Proactive', message: `${label} is required.` })),
  );
  if (report.fields.eventType === 'Other' && !String(report.fields.eventOther || '').trim()) {
    issues.push({ key: 'eventOther', label: 'Proactive', message: 'Other event type requires text.' });
  }
  if (report.fields.related.includes('Other') && !String(report.fields.relatedOther || '').trim()) {
    issues.push({ key: 'relatedOther', label: 'Proactive', message: 'Other related category requires text.' });
  }
  (report.fields.additionalParticipants || []).forEach((participant, index) => {
    if (!participant.name.trim()) {
      issues.push({
        key: `participant_${participant.id}_name`,
        label: 'Proactive',
        message: `Participant ${index + 1} name is required.`,
      });
    }
  });
  return issues;
};

type ReportPhotoEntry = {
  photo: PhotoRecord;
  label: string;
  context: string;
};

const reportFilename = (record: DraftRecord) =>
  `${record.type === 'critical' ? 'critical-control-report' : 'proactive-event-report'}-${record.id}.pdf`;

const criticalReportPhotoEntries = (session: CriticalSession): ReportPhotoEntry[] => {
  const attached = session.controlInstances.flatMap((control) =>
    activeResponses(control.responses).flatMap((response) =>
      photosForResponse(session.photos, response, control.id).map((photo, index) => ({
        photo,
        label: `${control.definitionSnapshot.code} item ${response.questionSnapshot.number} photo ${index + 1}`,
        context: response.questionSnapshot.text,
      })),
    ),
  );
  const general = session.photos
    .filter((photo) => photo.associationType === 'general')
    .map((photo, index) => ({
      photo,
      label: `General evidence photo ${index + 1}`,
      context: 'General evidence',
    }));
  return [...attached, ...general];
};

const proactiveReportPhotoEntries = (report: ProactiveReport): ReportPhotoEntry[] =>
  report.photos.map((photo, index) => ({
    photo,
    label: photoLabelFor(photo, `Proactive photo ${index + 1}`),
    context: report.fields.observation || 'Proactive report evidence',
  }));

const reportPhotoEntries = (record: DraftRecord) =>
  record.type === 'critical' ? criticalReportPhotoEntries(record) : proactiveReportPhotoEntries(record);

export const submissionReportFile = async (record: DraftRecord) => {
  const { jsPDF } = await import('jspdf');
  const doc = new jsPDF({ unit: 'pt', format: 'letter' });
  const margin = 42;
  const pageHeight = doc.internal.pageSize.getHeight();
  const pageWidth = doc.internal.pageSize.getWidth();
  const maxWidth = pageWidth - margin * 2;
  let y = margin;

  const addPage = () => {
    doc.addPage();
    y = margin;
  };

  const ensureSpace = (height: number) => {
    if (y + height > pageHeight - margin) addPage();
  };

  const addText = (text: string, options: { heading?: boolean; small?: boolean } = {}) => {
    const line = text.trim();
    if (!line) {
      y += 8;
      return;
    }
    const fontSize = options.heading ? 14 : options.small ? 9 : 10.5;
    const lineHeight = options.heading ? 17 : options.small ? 11 : 13;
    doc.setFont('helvetica', options.heading ? 'bold' : 'normal');
    doc.setFontSize(fontSize);
    const wrapped = doc.splitTextToSize(line, maxWidth) as string[];
    ensureSpace(wrapped.length * lineHeight + 4);
    doc.text(wrapped, margin, y);
    y += wrapped.length * lineHeight + 4;
  };

  const body = record.type === 'critical' ? buildCriticalEmailBody(record) : buildProactiveEmailBody(record);
  body.split('\n').forEach((line) => {
    const isHeading = Boolean(line.trim()) && line.trim() === line.trim().toUpperCase() && line.trim().length < 48;
    addText(line, { heading: isHeading });
  });

  const photos = reportPhotoEntries(record);
  addText('Supporting Photos', { heading: true });
  if (!photos.length) {
    addText('No photos attached.');
  }

  photos.forEach(({ photo, label, context }) => {
    addText(label, { heading: true });
    addText(context, { small: true });
    try {
      const properties = doc.getImageProperties(photo.dataUrl);
      const maxImageWidth = maxWidth;
      const maxImageHeight = 260;
      const ratio = Math.min(maxImageWidth / properties.width, maxImageHeight / properties.height, 1);
      const width = Math.max(1, properties.width * ratio);
      const height = Math.max(1, properties.height * ratio);
      ensureSpace(height + 18);
      doc.addImage(photo.dataUrl, 'JPEG', margin, y, width, height);
      y += height + 18;
    } catch {
      addText('Photo could not be embedded in this PDF package.', { small: true });
    }
  });

  const blob = doc.output('blob');
  return new File([blob], reportFilename(record), { type: 'application/pdf' });
};

const fileToBase64 = (file: File) =>
  new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(new Error('Unable to prepare the report attachment for email.'));
    reader.onload = () => {
      const result = String(reader.result || '');
      const commaIndex = result.indexOf(',');
      resolve(commaIndex === -1 ? result : result.slice(commaIndex + 1));
    };
    reader.readAsDataURL(file);
  });

export const prepareEmail = async (
  subject: string,
  body: string,
  recipients: string[],
  file?: File,
  photos: PhotoRecord[] = [],
) => {
  const reportAttachments = file
    ? [{
        filename: file.name,
        contentType: file.type || 'application/pdf',
        content: await fileToBase64(file),
      }]
    : [];
  const photoAttachments = photos.map((photo, index) => {
    const commaIndex = photo.dataUrl.indexOf(',');
    return {
      filename: `critical-control-photo-${index + 1}.jpg`,
      contentType: 'image/jpeg',
      content: commaIndex === -1 ? photo.dataUrl : photo.dataUrl.slice(commaIndex + 1),
      contentId: `critical-control-photo-${index + 1}`,
      label: photoLabelFor(photo, `Photo ${index + 1}`),
    };
  });
  const attachments = [...reportAttachments, ...photoAttachments];

  let response: Response;
  try {
    response = await fetch('/.netlify/functions/send-report', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ recipients, subject, body, attachments }),
    });
  } catch {
    throw new Error('Automatic email service could not be reached. The report was saved locally and was not sent.');
  }

  const result = await response.json().catch(() => ({}));
  if (!response.ok) {
    const detail = typeof result.error === 'string' && result.error.trim()
      ? result.error.trim()
      : `Email service returned HTTP ${response.status}`;
    throw new Error(`Automatic email failed: ${detail}. The report was saved locally and was not sent.`);
  }

  return {
    method: 'server_email' as const,
    attachmentsAttempted: attachments.length,
    attachmentsShared: attachments.length,
    id: typeof result.id === 'string' ? result.id : '',
  };
};

const namedHeading = (title: string) => [title.toUpperCase()];

const responsePhotoLine = (photos: PhotoRecord[]) =>
  photos.length ? `   Photo: ${photos.length} recorded` : '';

const participantLines = (session: CriticalSession) =>
  (session.sharedDetails.additionalParticipants || []).map((participant, index) =>
    `Participant ${index + 1}: ${participant.name}${participant.employeeNumber ? ` (${participant.employeeNumber})` : ''}`,
  );

export const buildCriticalEmailBody = (session: CriticalSession) => {
  const appUrl = currentAppUrl();
  const findings = session.controlInstances.flatMap((control) =>
    activeResponses(control.responses)
      .filter((response) => response.state === 'exposed' || response.flagged)
      .map((response) => ({ control, response })),
  );
  const lines: string[] = [
    ...namedHeading('Critical Control Report'),
    '',
    `Date: ${session.sharedDetails.date}`,
    `Time: ${session.sharedDetails.time}`,
    `Name: ${session.sharedDetails.observer}`,
    `PC / Branch: ${session.sharedDetails.pcBranch}`,
  ];

  if (session.sharedDetails.employeeNumber) lines.push(`Employee Number: ${session.sharedDetails.employeeNumber}`);
  lines.push(...participantLines(session));

  lines.push(
    '',
    'Controls Completed:',
    ...session.controlInstances.map((control) => `${control.definitionSnapshot.code} | ${control.definitionSnapshot.title}`),
    '',
    `Photos: ${photoCountLabel(session.photos.length)}`,
    '',
    ...namedHeading('Quick Results'),
  );

  session.controlInstances.forEach((control) => {
    lines.push('', `${control.definitionSnapshot.title.toUpperCase()} | ${control.definitionSnapshot.code}`, '');
    activeResponses(control.responses).forEach((response) => {
      const responsePhotos = photosForResponse(session.photos, response, control.id);
      lines.push(`${response.questionSnapshot.number}. ${responseLabel(response.state).toUpperCase()}${response.flagged ? ' | FLAGGED' : ''}`);
      if (response.comment) lines.push(`   Comment: ${response.comment}`);
      const photoLine = responsePhotoLine(responsePhotos);
      if (photoLine) lines.push(photoLine);
    });
  });

  lines.push('', ...namedHeading('Critical Findings / Flags'));

  if (findings.length) {
    findings.forEach(({ control, response }) => {
      const responsePhotos = photosForResponse(session.photos, response, control.id);
      lines.push(
        '',
        `${control.definitionSnapshot.code} | ${control.definitionSnapshot.title}`,
        `Item ${response.questionSnapshot.number}: ${responseLabel(response.state).toUpperCase()}${response.flagged ? ' | FLAGGED' : ''}`,
        response.questionSnapshot.text,
      );
      if (response.comment) lines.push(`Comment: ${response.comment}`);
      const photoLine = responsePhotoLine(responsePhotos);
      if (photoLine) lines.push(photoLine.trim());
      if (!responsePhotos.length) lines.push('Photo: none attached');
    });
  } else {
    lines.push('No Exposed items or manual flags recorded.');
  }

  lines.push('', ...namedHeading('Full Checklist Details'));

  session.controlInstances.forEach((control) => {
    lines.push('', `${control.definitionSnapshot.code} | ${control.definitionSnapshot.title}`);
    lines.push(`${control.definitionSnapshot.revision} ${control.definitionSnapshot.sourceDate}`);
    activeResponses(control.responses).forEach((response) => {
      const responsePhotos = photosForResponse(session.photos, response, control.id);
      lines.push('');
      lines.push(`${response.questionSnapshot.number}. ${response.questionSnapshot.text}`);
      lines.push(`   Response: ${responseLabel(response.state)}${response.flagged ? ' | Flagged' : ''}`);
      if (response.comment) lines.push(`   Comment: ${response.comment}`);
      const photoLine = responsePhotoLine(responsePhotos);
      if (photoLine) lines.push(photoLine);
    });
  });

  lines.push('', 'Critical Control Hub', `Link: ${appUrl}`);
  if (session.photos.length) lines.push('', 'Supporting photos are included in the attached PDF report.');
  return lines.join('\n');
};

export const buildProactiveEmailBody = (report: ProactiveReport) => {
  const appUrl = currentAppUrl();
  const levels = report.sourceSnapshot?.eventLevels || eventLevels;
  const selectedLevel = levels.find((level) => level.id === report.fields.eventLevel);
  const participants = report.fields.additionalParticipants || [];
  return [
    ...namedHeading('Proactive Event Report'),
    '',
    report.sourceSnapshot?.title || proactiveSource.title,
    proactiveSource.description,
    `Revision: ${report.sourceSnapshot?.revision || proactiveSource.revision}`,
    '',
    `PC / Branch: ${report.fields.pcNumber}`,
    `Date: ${report.fields.date}`,
    `Time: ${report.fields.time}`,
    `Name: ${report.fields.name}`,
    ...(report.fields.employeeNumber ? [`Employee Number: ${report.fields.employeeNumber}`] : []),
    ...participants.map((participant, index) => `Participant ${index + 1}: ${participant.name}${participant.employeeNumber ? ` (${participant.employeeNumber})` : ''}`),
    `Location of Event: ${report.fields.location}`,
    `Event: ${report.fields.eventType}${report.fields.eventOther ? ` ${report.fields.eventOther}` : ''}`,
    `Event Level: ${selectedLevel?.label || report.fields.eventLevel}`,
    `Related to: ${report.fields.related.join(', ')}${report.fields.relatedOther ? ` ${report.fields.relatedOther}` : ''}`,
    `Photos: ${photoCountLabel(report.photos.length)}`,
    '',
    ...namedHeading('Quick Results'),
    '',
    `Event: ${report.fields.eventType}${report.fields.eventOther ? ` ${report.fields.eventOther}` : ''}`,
    `Level: ${selectedLevel?.label || report.fields.eventLevel}`,
    `Related: ${report.fields.related.join(', ')}${report.fields.relatedOther ? ` ${report.fields.relatedOther}` : ''}`,
    '',
    ...namedHeading('Selected Event Level Definition'),
    '',
    selectedLevel ? `${selectedLevel.label}: ${selectedLevel.description}` : 'No level definition found for this saved report.',
    '',
    ...namedHeading('Observation'),
    '',
    report.fields.observation,
    '',
    ...namedHeading('Corrective Action'),
    '',
    report.fields.correctiveAction,
    '',
    'Critical Control Hub',
    `Link: ${appUrl}`,
    ...(report.photos.length ? ['', 'Supporting photos are included in the attached PDF report.'] : []),
  ].join('\n');
};

const photoHtml = (photos: PhotoRecord[], empty = 'No photos attached.') =>
  photos.length
    ? `<div class="photo_grid">${photos
        .map(
          (photo, index) =>
            `<figure><img src="${photo.dataUrl}" alt="${escapeHtml(photoLabelFor(photo, `Attached photo ${index + 1}`))}"><figcaption>${escapeHtml(photoLabelFor(photo, `Photo ${index + 1}`))}</figcaption></figure>`,
        )
        .join('')}</div>`
    : `<p class="empty_note">${escapeHtml(empty)}</p>`;

const supportingPhotoIndex = (session: CriticalSession) => {
  const rows = session.controlInstances.flatMap((control) =>
    activeResponses(control.responses).flatMap((response) =>
      photosForResponse(session.photos, response, control.id).map((photo, index) => ({
        photo,
        label: `${control.definitionSnapshot.code} item ${response.questionSnapshot.number} photo ${index + 1}`,
        question: response.questionSnapshot.text,
      })),
    ),
  );
  const generalPhotos = session.photos
    .filter((photo) => photo.associationType === 'general')
    .map((photo, index) => ({ photo, label: `General photo ${index + 1}`, question: 'General evidence' }));
  const allRows = [...rows, ...generalPhotos];
  if (!allRows.length) return '<p class="empty_note">No photos attached.</p>';
  return `<div class="supporting_photos">${allRows
    .map(
      ({ photo, label, question }) =>
        `<div class="photo_detail"><h3>${escapeHtml(label)}</h3><p>${escapeHtml(question)}</p>${photoHtml([photo])}</div>`,
    )
    .join('')}</div>`;
};

export const buildCriticalReportHtml = (session: CriticalSession) => {
  const findings = session.controlInstances.flatMap((control) =>
    activeResponses(control.responses)
      .filter((response) => response.state === 'exposed' || response.flagged)
      .map((response) => ({ control, response })),
  );
  const controls = session.controlInstances
    .map(
      (control) => `
      <section>
        <h2>${escapeHtml(control.definitionSnapshot.title)}</h2>
        <p><strong>${escapeHtml(control.definitionSnapshot.code)}</strong> ${escapeHtml(control.definitionSnapshot.revision)} ${escapeHtml(control.definitionSnapshot.sourceDate)}</p>
        ${Object.entries(control.editableDetails)
          .filter((entry) => entry[1])
          .map(([key, value]) => `<p><strong>${escapeHtml(key)}:</strong> ${escapeHtml(value)}</p>`)
          .join('')}
        ${control.definitionSnapshot.sections
          .map(
            (section) => `
            ${section.title ? `<h3>${escapeHtml(section.title)}</h3>` : ''}
            <table>
              <thead><tr><th>Item</th><th>Question</th><th>Response</th><th>Flag</th><th>Comment</th><th>Photos</th></tr></thead>
              <tbody>${control.responses
                .map((response) => {
                  const responsePhotos = photosForResponse(session.photos, response, control.id);
                  return `
                  <tr data-control-id="${escapeHtml(control.id)}" data-item-id="${escapeHtml(response.itemId)}">
                    <td>${escapeHtml(response.questionSnapshot.number)}</td>
                    <td>${escapeHtml(response.questionSnapshot.text || response.questionSnapshot.sourceNote || 'Blank source row')}</td>
                    <td>${response.questionSnapshot.inactive ? 'Inactive source row' : responseLabel(response.state)}</td>
                    <td>${response.flagged ? 'Flagged' : ''}</td>
                    <td>${escapeHtml(response.comment || '')}</td>
                    <td>${responsePhotos.length ? `${responsePhotos.length} attached` : 'No photos attached'}</td>
                  </tr>`;
                })
                .join('')}</tbody>
            </table>`,
          )
          .join('')}
      </section>`,
    )
    .join('');
  const participantHtml = (session.sharedDetails.additionalParticipants || [])
    .map((participant, index) => `<p><strong>Participant ${index + 1}:</strong> ${escapeHtml(participant.name)}${participant.employeeNumber ? ` (${escapeHtml(participant.employeeNumber)})` : ''}</p>`)
    .join('');
  return reportShell(
    'Critical Control Report',
    `
      <section class="report_header">
        <h1>Critical Control Report</h1>
        <p><strong>Date:</strong> ${escapeHtml(session.sharedDetails.date)} <strong>Time:</strong> ${escapeHtml(session.sharedDetails.time)}</p>
        <p><strong>Name:</strong> ${escapeHtml(session.sharedDetails.observer)} <strong>PC / Branch:</strong> ${escapeHtml(session.sharedDetails.pcBranch)}</p>
        ${session.sharedDetails.employeeNumber ? `<p><strong>Employee Number:</strong> ${escapeHtml(session.sharedDetails.employeeNumber)}</p>` : ''}
        ${participantHtml}
      </section>
      <section>
        <h2>Controls Completed</h2>
        <ul>${session.controlInstances.map((control) => `<li>${escapeHtml(control.definitionSnapshot.code)} | ${escapeHtml(control.definitionSnapshot.title)}</li>`).join('')}</ul>
      </section>
      <section>
        <h2>Critical Findings and Flags</h2>
        ${
          findings.length
            ? `<div class="finding_stack">${findings
                .map(({ control, response }) => {
                  const responsePhotos = photosForResponse(session.photos, response, control.id);
                  return `<article class="finding_card">
                    <h3>${escapeHtml(control.definitionSnapshot.title)} item ${escapeHtml(response.questionSnapshot.number)}</h3>
                    <p><strong>${responseLabel(response.state)}</strong>${response.flagged ? ' | Manual Flag' : ''}</p>
                    <p>${escapeHtml(response.questionSnapshot.text)}</p>
                    <p><strong>Comment:</strong> ${escapeHtml(response.comment || 'No comment entered.')}</p>
                    ${photoHtml(responsePhotos)}
                  </article>`;
                })
                .join('')}</div>`
            : '<p>No Exposed items or manual flags recorded.</p>'
        }
      </section>
      <section><h2>Supporting Photos</h2>${supportingPhotoIndex(session)}</section>
      ${controls}
    `,
  );
};

export const buildProactiveReportHtml = (report: ProactiveReport) =>
  {
    const levels = report.sourceSnapshot?.eventLevels || eventLevels;
    const selectedLevel = levels.find((level) => level.id === report.fields.eventLevel);
    const participantHtml = (report.fields.additionalParticipants || [])
      .map((participant, index) => `<p><strong>Participant ${index + 1}:</strong> ${escapeHtml(participant.name)}${participant.employeeNumber ? ` (${escapeHtml(participant.employeeNumber)})` : ''}</p>`)
      .join('');
    return reportShell(
      'Proactive Event Report',
      `
      <section class="report_header">
        <h1>${escapeHtml(report.sourceSnapshot?.title || proactiveSource.title)}</h1>
        <p>${escapeHtml(proactiveSource.description)}</p>
        <p><strong>Revision:</strong> ${escapeHtml(report.sourceSnapshot?.revision || proactiveSource.revision)}</p>
        <p><strong>PC / Branch:</strong> ${escapeHtml(report.fields.pcNumber)} <strong>Date:</strong> ${escapeHtml(report.fields.date)} <strong>Time:</strong> ${escapeHtml(report.fields.time)}</p>
        <p><strong>Name:</strong> ${escapeHtml(report.fields.name)}</p>
        ${report.fields.employeeNumber ? `<p><strong>Employee Number:</strong> ${escapeHtml(report.fields.employeeNumber)}</p>` : ''}
        ${participantHtml}
        <p><strong>Location of Event:</strong> ${escapeHtml(report.fields.location)}</p>
        <p><strong>Event:</strong> ${escapeHtml(report.fields.eventType)} ${escapeHtml(report.fields.eventOther || '')}</p>
        <p><strong>Event Level:</strong> ${escapeHtml(selectedLevel?.label || report.fields.eventLevel)}</p>
        <p><strong>Related to:</strong> ${escapeHtml(report.fields.related.join(', '))} ${escapeHtml(report.fields.relatedOther || '')}</p>
      </section>
      <section>
        <h2>Selected Event Level Definition</h2>
        ${selectedLevel ? `<p><strong>${escapeHtml(selectedLevel.label)}:</strong> ${escapeHtml(selectedLevel.description)}</p>` : '<p>No level definition found for this saved report.</p>'}
      </section>
      <section>
        <h2>Observation</h2><p>${escapeHtml(report.fields.observation)}</p>
        <h2>Corrective Action</h2><p>${escapeHtml(report.fields.correctiveAction)}</p>
      </section>
      <section>
        <h2>Event Level Reference</h2>
        <p>${escapeHtml(sifDefinition)}</p>
        <table><tbody>${levels
          .map((level) => `<tr><th>${escapeHtml(level.label)}</th><td>${escapeHtml(level.description)}</td></tr>`)
          .join('')}</tbody></table>
      </section>
      <section><h2>Photos / Evidence</h2>${photoHtml(report.photos)}</section>
      <section><h2>Source Categories</h2><p>${escapeHtml(relatedCategories.join(', '))}</p></section>
    `,
    );
  };

const reportShell = (title: string, body: string) => {
  const appUrl = currentAppUrl();
  const qr = qrDataUri(appUrl);
  return `<!doctype html>
<html lang="en">
<head>
  <meta charset="utf-8">
  <meta name="viewport" content="width=device-width, initial-scale=1">
  <title>${escapeHtml(title)}</title>
  <style>
    body { background: #f4f6f5; color: #111827; font-family: Arial, sans-serif; line-height: 1.45; margin: 0; padding: 18px; }
    .report_page { background: #fff; border: 1px solid #d6ddd8; margin: 0 auto; max-width: 720px; padding: 18px; }
    h1 { font-size: 26px; line-height: 1.15; margin: 0 0 14px; }
    h1, h2, h3 { color: #0f7a37; }
    h2 { border-top: 1px solid #d6ddd8; font-size: 18px; margin: 22px 0 10px; padding-top: 14px; }
    h3 { font-size: 15px; margin: 0 0 6px; }
    p { margin: 0 0 8px; }
    section { break-inside: avoid; margin-bottom: 18px; }
    table { border-collapse: collapse; width: 100%; margin: 12px 0; }
    th, td { border: 1px solid #cbd5e1; padding: 8px; text-align: left; vertical-align: top; }
    th { background: #eef7f0; color: #0f5132; }
    ul { margin-top: 8px; padding-left: 22px; }
    .finding_stack, .supporting_photos { display: grid; gap: 12px; }
    .finding_card, .photo_detail { border: 1px solid #d6ddd8; border-radius: 8px; padding: 12px; }
    .photo_grid { display: grid; gap: 12px; grid-template-columns: repeat(auto-fit, minmax(150px, 220px)); align-items: start; }
    figure { margin: 0; max-width: 220px; }
    img { display: block; width: 100%; max-width: 220px; max-height: 260px; object-fit: contain; border: 1px solid #cbd5e1; background: #f8fafc; }
    figcaption, .empty_note { color: #475569; font-size: 12px; margin-top: 4px; }
    .report_footer { align-items: center; border-top: 1px solid #d6ddd8; display: flex; gap: 16px; justify-content: space-between; margin-top: 24px; padding-top: 16px; }
    .report_footer a { color: #0f5132; overflow-wrap: anywhere; }
    .qr_code { height: 112px; width: 112px; }
    @media (max-width: 620px) { body { padding: 10px; } .report_page { padding: 14px; } .report_footer { align-items: flex-start; flex-direction: column; } table { font-size: 13px; } }
    @media print { body { background: #fff; padding: 0; } .report_page { border: 0; max-width: none; } }
  </style>
</head>
<body>
  <main class="report_page">
    ${body}
    <footer class="report_footer">
      <div>
        <strong>Critical Control Hub</strong>
        <p><a href="${escapeHtml(appUrl)}">${escapeHtml(appUrl)}</a></p>
      </div>
      ${qr ? `<img class="qr_code" data-qr-target="${escapeHtml(appUrl)}" src="${qr}" alt="QR code for Critical Control Hub">` : ''}
    </footer>
  </main>
</body>
</html>`;
};

export const downloadHtml = (html: string, filename: string) => {
  const blob = new Blob([html], { type: 'text/html;charset=utf-8' });
  const link = document.createElement('a');
  link.href = URL.createObjectURL(blob);
  link.download = filename;
  link.click();
  URL.revokeObjectURL(link.href);
};
